/**
 * @fileoverview Application Route Configuration for Clockwise CPA
 *
 * This module defines the complete routing structure for the Clockwise CPA application
 * using React Router v6. Routes are organized by feature area for better maintainability
 * and include comprehensive permission-based access control.
 *
 * Route Organization:
 * - clientRoutes: Client management and details
 * - timeEntryRoutes: Time tracking and entry management
 * - userRoutes: User management and profile pages
 * - invoiceRoutes: Billing and invoice management
 * - paymentRoutes: Payment processing and forms
 * - adminRoutes: Administrative functions
 * - miscRoutes: Support tickets and activity types
 *
 * Key Features:
 * - Permission-based route protection using ProtectedRoute component
 * - Nested routing structure with App as root component
 * - Error handling with dedicated error pages
 * - Dynamic route parameters for resource-specific pages
 * - Support for both create and edit forms with shared components
 *
 * Dependencies:
 * - React Router v6 for routing
 * - ProtectedRoute component for access control
 * - All page components and the main App component
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 * @since 2024-01-01
 */

import { lazy, Suspense } from 'react';
import App from "./App";
import Dashboard from "./pages/Dashboard";
import LoginPage from "./pages/LoginPage";
import ProtectedRoute from "./components/ProtectedRoute";
import PageLoader from "./components/PageLoader";
import RouteErrorBoundary from "./components/RouteErrorBoundary";

// Lazy load components for better performance
const ErrorPage = lazy(() => import("./pages/ErrorPage"));
const TimeEntryForm = lazy(() => import("./pages/TimeEntryForm"));
const Client = lazy(() => import("./pages/Client"));
const ClientForm = lazy(() => import("./pages/ClientForm"));
// eslint-disable-next-line no-unused-vars
const User = lazy(() => import("./pages/User"));
const UserForm = lazy(() => import("./pages/UserForm"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const ClientManagement = lazy(() => import("./pages/ClientManagement"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const BackupHistoryPage = lazy(() => import("./pages/BackupHistoryPage"));
const InvoicePage = lazy(() => import("./pages/Invoice"));
const ActivityTypesPage = lazy(() => import("./pages/ActivityTypesPage"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const WorkInProgressQueue = lazy(() => import("./pages/WIPQueue"));
const InvoicePaymentPage = lazy(() => import("./pages/InvoicePayment"));
const ClientActivities = lazy(() => import("./pages/ClientActivities"));
const Billing = lazy(() => import("./pages/Billing"));
const MyTimeEntries = lazy(() => import("./pages/TimeEntries"));
const InvoiceTextForm = lazy(() => import("./pages/InvoiceTextForm"));
const InvoiceTextManagement = lazy(() => import("./pages/InvoiceTextManagement"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const ReportBuilderPage = lazy(() => import("./pages/ReportBuilderPage")); // eslint-disable-line no-unused-vars
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const Changelog = lazy(() => import("./pages/Changelog"));
const PaymentsForm = lazy(() => import("./pages/PaymentsForm"));
// NotificationsPage removed - now uses modal-based inbox in NotificationCenter
const InvoiceForm = lazy(() => import("./pages/InvoiceForm"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AccountsReceivableLedger = lazy(() => import("./pages/AccountsRecievableLedger"));
const RoleManagement = lazy(() => import("./pages/RoleManagement"));
const SupportTickets = lazy(() => import("./pages/SupportTicketManagment"));
const SupportTicketForm = lazy(() => import("./pages/SupportTicketForm"));
const SupportTicketDetail = lazy(() => import("./pages/SupportTicket"));
const OrientationPage = lazy(() => import("./pages/OrientationPage"));
const ExportCenter = lazy(() => import("./pages/ExportCenter"));
const TestErrorBoundary = lazy(() => import("./pages/TestErrorBoundary"));
const NotificationBroadcast = lazy(() => import("./pages/NotificationBroadcast"));
const ClientPortalLogin = lazy(() => import("./pages/ClientPortal/ClientPortalLogin"));
const ClientPortalInvoice = lazy(() => import("./pages/ClientPortal/ClientPortalInvoice"));
const ClientPortalPayment = lazy(() => import("./pages/ClientPortal/ClientPortalPayment"));
// PublicStatusPage moved to status.clockwisecpa.app subdomain - imported directly in index.js
const LeaveBankWrapper = lazy(() => import("./components/LeaveBankWrapper"));
const UserDetail = lazy(() => import("./pages/UserDetail"));
const UIComponentsPage = lazy(() => import("./pages/UIComponentsPage"));

// Create a wrapper component for lazy loaded components with loading fallback
const LazyWrapper = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);


/**
 * Client-related routes
 * Routes for client management, details, and related functionality
 *
 * @type {Array<Object>}
 */
const clientRoutes = [
    {
        path: '/',
        element: (
            <RouteErrorBoundary routeName="Dashboard">
                <Dashboard />
            </RouteErrorBoundary>
        )
    },
    {
        path: '/clients/:id',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_clients">
                    <RouteErrorBoundary routeName="Client Details">
                        <Client />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/clients/create',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_clients">
                    <RouteErrorBoundary routeName="Client Form">
                        <ClientForm />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/clients/:id/edit',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_clients">
                    <RouteErrorBoundary routeName="Client Form">
                        <ClientForm />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/clients/manage',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_clients">
                    <RouteErrorBoundary routeName="Client Management">
                        <ClientManagement />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/clients/:clientId/wip',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_create_invoices">
                    <RouteErrorBoundary routeName="Work In Progress Queue">
                        <WorkInProgressQueue />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/clients/:id/activity-types',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_clients">
                    <ClientActivities />
                </ProtectedRoute>
            </LazyWrapper>
    },
];

/**
 * Time entry routes
 * Routes for time tracking, entry creation and editing
 *
 * @type {Array<Object>}
 */
const timeEntryRoutes = [
    {
        path: '/time_entries/:id/edit',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_time_entries">
                    <RouteErrorBoundary routeName="Time Entry Form">
                        <TimeEntryForm />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/time_entries/create',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_time_entries">
                    <RouteErrorBoundary routeName="Time Entry Form">
                        <TimeEntryForm />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    }
];

/**
 * User management routes
 * Routes for user profiles, management, and settings
 *
 * @type {Array<Object>}
 */
const userRoutes = [
    {
        path: '/users/create',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_users">
                    <UserForm />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/users/manage',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_users">
                    <UserManagement />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/users/:id',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_users">
                    <UserDetail />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/users/:userId/edit',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_users">
                    <UserForm />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/profile',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_time_entries">
                    <RouteErrorBoundary routeName="Time Entries">
                        <MyTimeEntries />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/leave-bank',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_time_banks" requiredRoleId={6}>
                    <LeaveBankWrapper />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/account',
        element:
            <LazyWrapper>
                <UserProfile />
            </LazyWrapper>
    },
    {
        path: '/settings',
        element:
            <LazyWrapper>
                <SettingsPage />
            </LazyWrapper>
    },
    {
        path: '/changelog',
        element:
            <LazyWrapper>
                <Changelog />
            </LazyWrapper>
    }
    // Notifications page removed - now uses modal-based inbox
];

/**
 * Miscellaneous routes
 * Routes for support tickets, activity types, and other features
 *
 * @type {Array<Object>}
 */
const miscRoutes = [
    {
        path: '/activity-types',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_clients">
                    <ActivityTypesPage />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/support-tickets',
        element:
            <LazyWrapper>
                <SupportTickets/>
            </LazyWrapper>
    },
    {
        path:'/support-tickets/create',
        element:
            <LazyWrapper>
                <SupportTicketForm/>
            </LazyWrapper>
    },
    {
        path:'/support-tickets/:ticketId',
        element:
            <LazyWrapper>
                <SupportTicketDetail/>
            </LazyWrapper>
    },
    {
        path: '/orientation',
        element:
            <LazyWrapper>
                <OrientationPage/>
            </LazyWrapper>
    }
]

/**
 * Invoice and billing routes
 * Routes for invoice management, billing, reporting, and accounts receivable
 *
 * @type {Array<Object>}
 */
const invoiceRoutes = [
    {
        path: '/invoices/preview',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_create_invoices">
                    <RouteErrorBoundary routeName="Invoice Form">
                        <InvoiceForm />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/invoices/:id',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_invoices">
                    <RouteErrorBoundary routeName="Invoice Details">
                        <InvoicePage />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/invoices/:id/edit',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_invoices">
                    <RouteErrorBoundary routeName="Invoice Form">
                        <InvoiceForm />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/invoice_texts/create',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_create_invoices">
                    <InvoiceTextForm />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/invoice_texts/:id/edit',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_invoices">
                    <InvoiceTextForm />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/invoice_texts/manage',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_invoices">
                    <InvoiceTextManagement />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/billing',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_billing">
                    <RouteErrorBoundary routeName="Billing">
                        <Billing />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/billing/:refresh',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_billing">
                    <RouteErrorBoundary routeName="Billing">
                        <Billing />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/reports',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_reports_page">
                    <ReportsPage />
                </ProtectedRoute>
            </LazyWrapper>
    },
    // TEMPORARILY DISABLED - Custom Report Builder
    // {
    //     path: '/reports/builder',
    //     element:
    //         <LazyWrapper>
    //             <ProtectedRoute requiredPermission="can_view_reports_page">
    //                 <ReportBuilderPage />
    //             </ProtectedRoute>
    //         </LazyWrapper>
    // },
    {
        path: '/ledger',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_ar_ledger">
                    <RouteErrorBoundary routeName="Accounts Receivable Ledger">
                        <AccountsReceivableLedger />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    }
]

/**
 * Administrative routes
 * Routes for admin functions, role management, and password reset
 *
 * @type {Array<Object>}
 */
const adminRoutes = [
    {
        path: '/admin/roles',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="all">
                    <RoleManagement />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/admin/export',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="all">
                    <ExportCenter />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/admin/notifications/broadcast',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_broadcast_notifications">
                    <NotificationBroadcast />
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/admin/backups',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="all">
                    <BackupHistoryPage />
                </ProtectedRoute>
            </LazyWrapper>
    },
]

/**
 * Status page administrative routes
 * NOTE: Status management has been moved to admin backend server (admin.clockwisecpa.app)
 * Only public status page remains in main app
 *
 * @type {Array<Object>}
 */
const statusRoutes = []

/**
 * Payment processing routes
 * Routes for payment management and processing
 *
 * @type {Array<Object>}
 */
const paymentRoutes = [
    {
        path: '/clients/:clientId/payments',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_view_payments">
                    <RouteErrorBoundary routeName="Invoice Payment">
                        <InvoicePaymentPage />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    },
    {
        path: '/payments/:id/edit',
        element:
            <LazyWrapper>
                <ProtectedRoute requiredPermission="can_edit_payments">
                    <RouteErrorBoundary routeName="Payment Form">
                        <PaymentsForm />
                    </RouteErrorBoundary>
                </ProtectedRoute>
            </LazyWrapper>
    }
]

/**
 * Main application routes configuration
 * Combines all route groups under the main App component with error handling
 *
 * @type {Array<Object>}
 */
const routes = [
    {
        path: '/',
        element: <App />,
        errorElement: <ErrorPage />,
        children: [
            ...clientRoutes,
            ...timeEntryRoutes,
            ...userRoutes,
            ...paymentRoutes,
            ...invoiceRoutes,
            ...miscRoutes,
            ...adminRoutes,
            ...statusRoutes,
            {
                path: '/admin',
                element:
                    <LazyWrapper>
                        <ProtectedRoute requiredPermission="can_view_admin_page">
                            <AdminPage />
                        </ProtectedRoute>
                    </LazyWrapper>
            },
            {
                path: '/error',
                element: <LazyWrapper><ErrorPage /></LazyWrapper>
            },
            {
                path: '/access-denied',
                element:
                    <LazyWrapper>
                        <AccessDenied />
                    </LazyWrapper>
            },
            {
                path: '/test-error',
                element:
                    <LazyWrapper>
                        <TestErrorBoundary />
                    </LazyWrapper>
            },
            {
                path: '/ui-components',
                element:
                    <LazyWrapper>
                        <UIComponentsPage />
                    </LazyWrapper>
            },
        ]
    },
    {
        path: '/login',
        element: <LoginPage />
    },
    {
        path: '/reset_password/:resetCode',
        element:
            <LazyWrapper>
                <ResetPasswordPage />
            </LazyWrapper>
    },
    {
        path: '/client-portal/:accessCode',
        element:
            <LazyWrapper>
                <ClientPortalLogin />
            </LazyWrapper>
    },
    {
        path: '/client-portal/invoice',
        element:
            <LazyWrapper>
                <ClientPortalInvoice />
            </LazyWrapper>
    },
    {
        path: '/client-portal/payment',
        element:
            <LazyWrapper>
                <ClientPortalPayment />
            </LazyWrapper>
    }
    // Status page moved to status.clockwisecpa.app subdomain
];

/**
 * Export the complete routes configuration
 * Used by React Router to configure application navigation
 *
 * @exports routes
 */
export default routes;